import React, { useState, useEffect, useMemo } from 'react';
import { Users, Power, PowerOff, ShieldAlert, Loader2, Search, UserPlus, Pencil, Trash2, KeyRound, CheckCircle2, XCircle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function AdminDashboard() {
    const { getAuthHeaders } = useAuth();
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null); // {type, msg}
    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState('');

    const [newUser, setNewUser] = useState({ email: '', password: '', ruc: '', companyName: '', sinConfiguracion: false });
    const [creating, setCreating] = useState(false);
    const [editingUser, setEditingUser] = useState(null); // { ...user, password? }

    const flash = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3500); };

    useEffect(() => { fetchUsers(); }, []);

    const fetchUsers = async () => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_URL}/api/admin/users`, { headers });
            if (!response.ok) throw new Error('No tienes permisos de administrador');
            const data = await response.json();
            if (data.success) setUsersList(data.users);
        } catch (err) { setError(err.message); }
        finally { setLoading(false); }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_URL}/api/admin/users/create`, {
                method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser)
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            flash('ok', 'Cliente creado y bloqueado a su RUC');
            setShowForm(false);
            setNewUser({ email: '', password: '', ruc: '', companyName: '', sinConfiguracion: false });
            fetchUsers();
        } catch (err) { flash('err', 'Error al crear: ' + err.message); }
        finally { setCreating(false); }
    };

    const toggleStatus = async (id) => {
        const prev = [...usersList];
        setUsersList(l => l.map(u => u.id === id ? { ...u, active: !u.active } : u));
        try {
            const headers = await getAuthHeaders();
            const r = await fetch(`${API_URL}/api/admin/users/${id}/toggle`, { method: 'POST', headers });
            const data = await r.json();
            if (!data.success) throw new Error(data.error);
        } catch (err) { setUsersList(prev); flash('err', 'No se pudo cambiar el estado'); }
    };

    const deleteUser = async (u) => {
        if (!window.confirm(`¿Eliminar a "${u.companyName || u.email}" por completo? Esto destruye su acceso y sus expedientes.`)) return;
        try {
            const headers = await getAuthHeaders();
            const r = await fetch(`${API_URL}/api/admin/users/${u.id}`, { method: 'DELETE', headers });
            const data = await r.json();
            if (!data.success) throw new Error(data.error);
            setUsersList(l => l.filter(x => x.id !== u.id));
            flash('ok', 'Cliente eliminado');
        } catch (err) { flash('err', 'Error al eliminar: ' + err.message); }
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        try {
            const headers = await getAuthHeaders();
            const r = await fetch(`${API_URL}/api/admin/users/${editingUser.id}`, {
                method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ruc: editingUser.ruc, companyName: editingUser.companyName, password: editingUser.password || '', sinConfiguracion: !!editingUser.sinConfiguracion })
            });
            const data = await r.json();
            if (!data.success) throw new Error(data.error);
            setUsersList(l => l.map(u => u.id === editingUser.id ? { ...u, ruc: editingUser.ruc, companyName: editingUser.companyName, sinConfiguracion: !!editingUser.sinConfiguracion } : u));
            flash('ok', editingUser.password ? 'Cliente actualizado y contraseña restablecida' : 'Cliente actualizado');
            setEditingUser(null);
        } catch (err) { flash('err', 'Error al editar: ' + err.message); }
    };

    const stats = useMemo(() => ({
        total: usersList.length,
        activos: usersList.filter(u => u.active).length,
        suspendidos: usersList.filter(u => !u.active).length,
    }), [usersList]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return usersList;
        return usersList.filter(u =>
            (u.email || '').toLowerCase().includes(q) ||
            (u.ruc || '').toLowerCase().includes(q) ||
            (u.companyName || '').toLowerCase().includes(q));
    }, [usersList, search]);

    if (error) {
        return (
            <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', padding: '4rem' }}>
                <ShieldAlert size={64} style={{ color: '#ef4444', margin: '0 auto 1rem' }} />
                <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>Acceso Denegado</h1>
                <p style={{ color: 'hsl(var(--muted-foreground))', marginTop: '1rem' }}>No tienes los privilegios necesarios para ver esta sección.</p>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {toast && (
                <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 100, display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', borderRadius: 'var(--radius)', background: toast.type === 'ok' ? '#10b981' : '#ef4444', color: '#fff', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', fontSize: '0.875rem', fontWeight: 600 }}>
                    {toast.type === 'ok' ? <CheckCircle2 size={16} /> : <XCircle size={16} />} {toast.msg}
                </div>
            )}

            {/* Header + stats */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <ShieldAlert size={30} style={{ color: '#ef4444' }} />
                <div>
                    <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>Panel de Control KREO</h1>
                    <p style={{ color: 'hsl(var(--muted-foreground))' }}>Gestión de clientes del sistema El Gem.</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <StatCard label="Clientes" value={stats.total} icon={Users} color="#3b82f6" />
                <StatCard label="Activos" value={stats.activos} icon={CheckCircle2} color="#10b981" />
                <StatCard label="Suspendidos" value={stats.suspendidos} icon={XCircle} color="#ef4444" />
            </div>

            <div className="card" style={{ padding: '1.5rem' }}>
                {/* Toolbar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: '220px', maxWidth: '360px' }}>
                        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'hsl(var(--muted-foreground))' }} />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por empresa, RUC o email..."
                            style={{ width: '100%', padding: '0.6rem 0.75rem 0.6rem 2.2rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', fontSize: '0.875rem' }} />
                    </div>
                    <button onClick={() => { setShowForm(s => !s); setEditingUser(null); }} style={primaryBtn}>
                        {showForm ? <><X size={16} /> Cancelar</> : <><UserPlus size={16} /> Registrar cliente</>}
                    </button>
                </div>

                {/* Form edición */}
                {editingUser && (
                    <form onSubmit={handleEditUser} style={formBox('#3b82f6')}>
                        <h4 style={{ marginBottom: '1.25rem', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Pencil size={18} /> Editar: {editingUser.email}</h4>
                        <div style={formGrid}>
                            <Input label="RUC" value={editingUser.ruc || ''} onChange={v => setEditingUser({ ...editingUser, ruc: v })} required />
                            <Input label="Nombre de Empresa" value={editingUser.companyName || ''} onChange={v => setEditingUser({ ...editingUser, companyName: v })} required />
                            <Input label="Nueva contraseña (opcional)" value={editingUser.password || ''} onChange={v => setEditingUser({ ...editingUser, password: v })} placeholder="Dejar vacío para no cambiar" icon={KeyRound} />
                        </div>
                        <Check label="Sin acceso a Configuración (sub-cuenta)" checked={!!editingUser.sinConfiguracion} onChange={v => setEditingUser({ ...editingUser, sinConfiguracion: v })} />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button type="button" onClick={() => setEditingUser(null)} style={ghostBtn}>Cancelar</button>
                            <button type="submit" style={{ ...primaryBtn, background: '#3b82f6' }}>Guardar cambios</button>
                        </div>
                    </form>
                )}

                {/* Form creación */}
                {showForm && (
                    <form onSubmit={handleCreateUser} style={formBox('hsl(var(--border))')}>
                        <h4 style={{ marginBottom: '1.25rem', color: 'hsl(var(--primary))', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><UserPlus size={18} /> Nuevo cliente</h4>
                        <div style={formGrid}>
                            <Input label="Email de acceso" type="email" value={newUser.email} onChange={v => setNewUser({ ...newUser, email: v })} placeholder="cliente@empresa.com" required />
                            <Input label="Contraseña inicial" value={newUser.password} onChange={v => setNewUser({ ...newUser, password: v })} placeholder="Segura123!" required />
                            <Input label="RUC (candado anti-subarriendo)" value={newUser.ruc} onChange={v => setNewUser({ ...newUser, ruc: v })} placeholder="20000000000" required />
                            <Input label="Nombre de empresa" value={newUser.companyName} onChange={v => setNewUser({ ...newUser, companyName: v })} placeholder="Razón Social S.A.C." required />
                        </div>
                        <Check label="Sin acceso a Configuración (sub-cuenta de la empresa)" checked={newUser.sinConfiguracion} onChange={v => setNewUser({ ...newUser, sinConfiguracion: v })} />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button type="button" onClick={() => setShowForm(false)} style={ghostBtn}>Cancelar</button>
                            <button type="submit" disabled={creating} style={{ ...primaryBtn, background: '#10b981' }}>
                                {creating ? <><Loader2 size={16} className="animate-spin" /> Registrando...</> : 'Guardar y autorizar'}
                            </button>
                        </div>
                    </form>
                )}

                {/* Tabla */}
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>
                        <Loader2 className="animate-spin" /> Cargando clientes...
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={{ padding: '2.5rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
                        {search ? 'Sin resultados para tu búsqueda.' : 'Aún no hay clientes registrados.'}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '720px' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid hsl(var(--border))', fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <th style={th}>Cliente</th>
                                    <th style={th}>RUC</th>
                                    <th style={th}>Rol</th>
                                    <th style={th}>Estado</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(u => {
                                    const initials = (u.companyName || u.email || '?').slice(0, 2).toUpperCase();
                                    return (
                                        <tr key={u.id} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                                            <td style={td}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #3884ff, #965aff)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.78rem', flexShrink: 0 }}>{initials}</div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.companyName || '—'}</div>
                                                        <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>{u.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={td}>{u.ruc || '—'}</td>
                                            <td style={td}>
                                                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                                                    <Pill bg={u.role === 'admin' ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.12)'} color={u.role === 'admin' ? '#ef4444' : '#3b82f6'}>{(u.role || 'user').toUpperCase()}</Pill>
                                                    {u.sinConfiguracion && <Pill bg="rgba(245,158,11,0.14)" color="#b45309">SUB-CUENTA</Pill>}
                                                </div>
                                            </td>
                                            <td style={td}>
                                                <Pill bg={u.active ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)'} color={u.active ? '#10b981' : '#ef4444'}>
                                                    <span style={{ width: 6, height: 6, borderRadius: 999, background: u.active ? '#10b981' : '#ef4444', display: 'inline-block', marginRight: 5 }} />
                                                    {u.active ? 'Activo' : 'Suspendido'}
                                                </Pill>
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                                <div style={{ display: 'inline-flex', gap: '0.4rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                    {u.role !== 'admin' && (
                                                        <>
                                                            <IconBtn onClick={() => { setEditingUser({ ...u, password: '' }); setShowForm(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} color="#3b82f6" title="Editar"><Pencil size={15} /></IconBtn>
                                                            <button onClick={() => toggleStatus(u.id)} style={{ ...miniBtn, color: u.active ? '#ef4444' : '#10b981', background: u.active ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)' }}>
                                                                {u.active ? <><PowerOff size={14} /> Desactivar</> : <><Power size={14} /> Activar</>}
                                                            </button>
                                                            <IconBtn onClick={() => deleteUser(u)} color="#ef4444" title="Eliminar"><Trash2 size={15} /></IconBtn>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="card" style={{ padding: '1.1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
                <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.2rem' }}>{label}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 800 }}>{value}</div>
            </div>
            <div style={{ padding: '0.5rem', background: color + '1a', color, borderRadius: 'var(--radius)' }}><Icon size={20} /></div>
        </div>
    );
}

const Input = ({ label, value, onChange, type = 'text', placeholder, required, icon: Icon }) => (
    <div>
        <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.82rem', fontWeight: 500 }}>{label}</label>
        <div style={{ position: 'relative' }}>
            {Icon && <Icon size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'hsl(var(--muted-foreground))' }} />}
            <input type={type} required={required} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)}
                style={{ width: '100%', padding: Icon ? '0.65rem 0.9rem 0.65rem 2.1rem' : '0.65rem 0.9rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', fontSize: '0.875rem' }} />
        </div>
    </div>
);

const Check = ({ label, checked, onChange }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem', fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }} />
        {label}
    </label>
);

const Pill = ({ children, bg, color }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0.22rem 0.6rem', borderRadius: 999, background: bg, color, fontSize: '0.72rem', fontWeight: 700 }}>{children}</span>
);
const IconBtn = ({ children, onClick, color, title }) => (
    <button onClick={onClick} title={title} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 'var(--radius)', border: `1px solid ${color}40`, background: color + '12', color, cursor: 'pointer' }}>{children}</button>
);

const th = { padding: '0.75rem 1rem' };
const td = { padding: '0.85rem 1rem', fontSize: '0.9rem', verticalAlign: 'middle' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.1rem', background: 'hsl(var(--primary))', color: 'white', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' };
const ghostBtn = { padding: '0.6rem 1.2rem', background: 'transparent', color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '0.875rem' };
const miniBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0 0.7rem', height: 32, border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' };
const formBox = (border) => ({ padding: '1.5rem', background: 'hsl(var(--background))', border: `1px solid ${border}`, borderRadius: 'var(--radius)', marginBottom: '1.5rem' });
const formGrid = { display: 'grid', gap: '1.1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', marginBottom: '1.25rem' };
